import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axios';
import { 
  Calendar, 
  DollarSign, 
  User, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Eye,
  Users,
  CreditCard,
  Clock
} from 'lucide-react';

function Subscriptions({ selectedInstance }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [chargebeeInstances, setChargebeeInstances] = useState([]);
  const [localSelectedInstance, setLocalSelectedInstance] = useState(selectedInstance || '');
  const [statusFilters, setStatusFilters] = useState({
    active: true,
    non_renewing: true,
    cancelled: true,
    in_trial: true,
    future: true,
    paused: true
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchChargebeeInstances();
  }, []);

  useEffect(() => {
    if (localSelectedInstance) {
      fetchSubscriptions();
    }
  }, [localSelectedInstance, statusFilters]);

  useEffect(() => {
    if (selectedInstance && selectedInstance !== localSelectedInstance) {
      setLocalSelectedInstance(selectedInstance);
    }
  }, [selectedInstance]);

  const fetchChargebeeInstances = async () => {
    try {
      const response = await axiosInstance.get('/api/chargebee-instances');
      setChargebeeInstances(response.data);
      if (!localSelectedInstance && response.data.length > 0) {
        // Find the test instance and set it as default
        const testInstance = response.data.find(instance => instance.id === 'test');
        if (testInstance) {
          setLocalSelectedInstance('test');
        } else {
          setLocalSelectedInstance(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching Chargebee instances:', error);
    }
  };

  const fetchSubscriptions = async () => {
    if (!localSelectedInstance) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/api/subscriptions', {
        params: {
          instance: localSelectedInstance,
          limit: 50,
          'status[]': Object.keys(statusFilters).filter(status => statusFilters[status])
        }
      });
      
      console.log('Subscriptions response:', response.data);
      
      // Handle different response formats from Chargebee
      let subscriptionsList = [];
      if (response.data.subscriptions && Array.isArray(response.data.subscriptions)) {
        subscriptionsList = response.data.subscriptions;
      } else if (response.data.list) {
        // Chargebee returns data in a 'list' array
        subscriptionsList = response.data.list.map(item => item.subscription || item);
      } else if (Array.isArray(response.data)) {
        subscriptionsList = response.data;
      }
      
      console.log('Processed subscriptions:', subscriptionsList);
      setSubscriptions(subscriptionsList);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to fetch subscriptions: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'non_renewing':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'in_trial':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'future':
        return <Calendar className="h-5 w-5 text-purple-500" />;
      case 'paused':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'non_renewing':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_trial':
        return 'bg-blue-100 text-blue-800';
      case 'future':
        return 'bg-purple-100 text-purple-800';
      case 'paused':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount / 100);
  };

  const handleViewDetails = async (subscription) => {
    setSelectedSubscription(subscription);
    setShowDetails(true);
  };

  const handleInstanceChange = (e) => {
    setLocalSelectedInstance(e.target.value);
  };

  const handleStatusToggle = (status) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const getActiveFiltersCount = () => {
    return Object.values(statusFilters).filter(Boolean).length;
  };

  const statusLabels = {
    active: 'Active',
    non_renewing: 'Non-Renewing', 
    cancelled: 'Cancelled',
    in_trial: 'In Trial',
    future: 'Future',
    paused: 'Paused'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscriptions</h2>
          <p className="text-gray-600">
            {localSelectedInstance && subscriptions.length > 0 
              ? `Showing ${subscriptions.length} subscriptions (${Object.keys(statusFilters).filter(status => statusFilters[status]).map(s => statusLabels[s]).join(', ')})` 
              : 'Live Chargebee subscription data'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={localSelectedInstance}
            onChange={handleInstanceChange}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Instance</option>
            {chargebeeInstances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.name} ({instance.region})
              </option>
            ))}
          </select>
          <button
            onClick={fetchSubscriptions}
            disabled={loading || !localSelectedInstance}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filters ({getActiveFiltersCount()})</span>
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Status Filters</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(statusLabels).map(([status, label]) => (
              <label key={status} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={statusFilters[status]}
                  onChange={() => handleStatusToggle(status)}
                  className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              onClick={() => setStatusFilters({
                active: true,
                non_renewing: true,
                cancelled: true,
                in_trial: true,
                future: true,
                paused: true
              })}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Select All
            </button>
            <button
              onClick={() => setStatusFilters({
                active: false,
                non_renewing: false,
                cancelled: false,
                in_trial: false,
                future: false,
                paused: false
              })}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Clear All
            </button>
            <button
              onClick={() => setStatusFilters({
                active: true,
                non_renewing: false,
                cancelled: false,
                in_trial: true,
                future: false,
                paused: false
              })}
              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
            >
              Active Only
            </button>
          </div>
        </div>
      )}

      {/* No Instance Selected */}
      {!localSelectedInstance && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Please select a Chargebee instance to view subscriptions</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && localSelectedInstance && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading subscriptions...</p>
        </div>
      )}

      {/* Subscriptions Grid */}
      {!loading && localSelectedInstance && subscriptions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptions.map((subscription) => (
            <div key={subscription.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              {/* Subscription Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {subscription.customer?.company || subscription.customer?.first_name || subscription.customer_id}
                  </h3>
                  <p className="text-sm text-gray-500">{subscription.id}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                  {subscription.status}
                </span>
              </div>

              {/* Plan Info */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {subscription.subscription_items?.[0]?.item_price_id || 'Plan'}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {subscription.subscription_items?.[0]?.quantity || 1} units
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {formatCurrency(subscription.mrr || 0, subscription.currency_code)}
                    /{subscription.billing_period_unit || 'month'}
                  </span>
                </div>

                {subscription.current_term_end && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">
                      Ends {formatDate(subscription.current_term_end)}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleViewDetails(subscription)}
                  className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  <span className="text-sm">View Details</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && localSelectedInstance && subscriptions.length === 0 && !error && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No subscriptions found</h3>
          <p className="text-gray-600">No subscriptions are available for this Chargebee instance.</p>
        </div>
      )}

      {/* Subscription Details Modal */}
      {showDetails && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedSubscription.customer?.company || selectedSubscription.customer?.first_name || 'Subscription Details'}
                  </h3>
                  <p className="text-gray-600">{selectedSubscription.id}</p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Subscription Details */}
              <div className="space-y-6">
                {/* Status */}
                <div className="flex items-center space-x-3">
                  {getStatusIcon(selectedSubscription.status)}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedSubscription.status)}`}>
                    {selectedSubscription.status.toUpperCase()}
                  </span>
                </div>

                {/* Customer Info */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p><span className="font-medium">Company:</span> {selectedSubscription.customer?.company || 'N/A'}</p>
                    <p><span className="font-medium">Contact:</span> {selectedSubscription.customer?.first_name} {selectedSubscription.customer?.last_name}</p>
                    <p><span className="font-medium">Email:</span> {selectedSubscription.customer?.email || 'N/A'}</p>
                    <p><span className="font-medium">Customer ID:</span> {selectedSubscription.customer_id}</p>
                  </div>
                </div>

                {/* Plan Details */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Plan Details</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p><span className="font-medium">Plan:</span> {selectedSubscription.subscription_items?.[0]?.item_price_id || 'N/A'}</p>
                    <p><span className="font-medium">Quantity:</span> {selectedSubscription.subscription_items?.[0]?.quantity || 1} units</p>
                    <p><span className="font-medium">Unit Price:</span> {formatCurrency(selectedSubscription.subscription_items?.[0]?.unit_price || 0, selectedSubscription.currency_code)}</p>
                    <p><span className="font-medium">MRR:</span> {formatCurrency(selectedSubscription.mrr || 0, selectedSubscription.currency_code)}</p>
                    <p><span className="font-medium">Billing:</span> Every {selectedSubscription.billing_period} {selectedSubscription.billing_period_unit}(s)</p>
                    {selectedSubscription.subscription_items?.length > 1 && (
                      <div className="mt-2">
                        <p className="font-medium">Add-ons:</p>
                        {selectedSubscription.subscription_items.slice(1).map((item, index) => (
                          <p key={index} className="text-sm text-gray-600 ml-4">
                            {item.item_price_id}: {formatCurrency(item.amount, selectedSubscription.currency_code)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Billing Info */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Billing Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {selectedSubscription.current_term_start && (
                      <p><span className="font-medium">Current Term Start:</span> {formatDate(selectedSubscription.current_term_start)}</p>
                    )}
                    {selectedSubscription.current_term_end && (
                      <p><span className="font-medium">Current Term End:</span> {formatDate(selectedSubscription.current_term_end)}</p>
                    )}
                    {selectedSubscription.next_billing_at && (
                      <p><span className="font-medium">Next Billing:</span> {formatDate(selectedSubscription.next_billing_at)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscriptions;